import React, { useEffect, useRef, useState } from 'react';
import { Download, Loader2, RotateCcw } from 'lucide-react';
import generationApi, { errorMessage, isMissingMedia, mediaErrorMessage, mediaUrl } from '../lib/generationApi';

export default function FinalFilm({ jobId, artifact, title = 'Your final film' }) {
  const filename = artifact.filename || 'final.mp4';
  const [attempt, setAttempt] = useState(0);
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('preparing');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [missing, setMissing] = useState(false);
  const lifetime = useRef(null);

  useEffect(() => {
    const session = { alive: true, fallback: false, objectUrl: null };
    lifetime.current = session;
    setSource(''); setError(''); setMissing(false); setStatus('preparing');
    generationApi.prepareMedia().then(() => {
      if (session.alive) { setSource(mediaUrl(jobId, artifact.artifact_id)); setStatus('loading'); }
    }).catch(err => { if (session.alive) { setError(errorMessage(err)); setStatus('error'); } });
    // Keep range requests authenticated even when a film is left open for a while.
    const timer = setInterval(() => { generationApi.prepareMedia().catch(() => {}); }, 10 * 60 * 1000);
    return () => {
      session.alive = false; clearInterval(timer);
      if (session.objectUrl) URL.revokeObjectURL(session.objectUrl);
    };
  }, [jobId, artifact.artifact_id, attempt]);

  async function recoverPlayback() {
    const session = lifetime.current;
    if (!session?.alive) return;
    if (session.fallback) {
      setError('This video could not be played. Try again or download the film.'); setStatus('error'); return;
    }
    session.fallback = true;
    setStatus('recovering');
    try {
      const blob = await generationApi.media(jobId, artifact.artifact_id);
      if (!session.alive) return;
      session.objectUrl = URL.createObjectURL(blob);
      setSource(session.objectUrl); setStatus('loading');
    } catch (err) { if (session.alive) { setError(mediaErrorMessage(err)); setMissing(isMissingMedia(err)); setStatus('error'); } }
  }

  async function download(event) {
    event.preventDefault();
    if (downloading || missing) return;
    setDownloading(true); setError('');
    try {
      if (!lifetime.current?.objectUrl) await generationApi.prepareMedia();
      const link = document.createElement('a');
      link.href = lifetime.current?.objectUrl || mediaUrl(jobId, artifact.artifact_id, true);
      link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { setError(errorMessage(err)); }
    finally { setDownloading(false); }
  }

  return <section className="studio-panel studio-film" aria-label="Finished film">
    <div className="studio-film-heading"><div><h2>{title}</h2><p>Ready to play. Ready to take further.</p></div><span className="studio-state studio-state-completed">Ready</span></div>
    <div className="studio-film-stage" aria-busy={['preparing', 'loading', 'recovering'].includes(status)}>
      {source && <video key={source} controls playsInline preload="metadata" src={source} onLoadedData={() => setStatus('ready')} onError={recoverPlayback} />}
      {['preparing', 'loading', 'recovering'].includes(status) && <p className="studio-film-loading" role="status"><Loader2 size={20} className="studio-spin" aria-hidden />{status === 'recovering' ? 'Loading your film securely…' : 'Preparing your film…'}</p>}
    </div>
    {error && <p role="alert" className="studio-alert studio-alert-error">{error}</p>}
    <div className="studio-film-actions"><span className="studio-caption">{(artifact.mime_type || 'video/mp4').split('/')[1]?.toUpperCase()}{artifact.size_bytes ? ` · ${(artifact.size_bytes / 1_000_000).toFixed(1)} MB` : ''}</span><div>{status === 'error' && <button type="button" className="studio-button studio-button-secondary" onClick={() => setAttempt(value => value + 1)}><RotateCcw size={16} aria-hidden />Retry playback</button>}<a href={missing ? undefined : lifetime.current?.objectUrl || mediaUrl(jobId, artifact.artifact_id, true)} download={filename} onClick={download} aria-disabled={downloading || missing} className="studio-button studio-button-primary"><Download size={17} aria-hidden />{downloading ? 'Preparing download…' : `Download ${filename}`}</a></div></div>
  </section>;
}
